/*
 * WARNING: This file is automatically maintained by the `yarn generate` command.
 * Changes must be made to the package.templateroot, or they will be lost!
 */
import { getDefaultClient, IRequestOptions, IRetryParams, ITimeoutParams } from '@ifit/gateway-core';
import type * as Axios from 'axios';
import type * as ReactQuery from 'react-query';
import type { IStationaryConsoleUpdateRequestBody } from '../models/stationary-console-update-request-body';
import type { IStationaryConsoleUpdateResponseOK } from '../models/stationary-console-update-response-ok';
import { consoleStoreIsDown } from './is-down';

type Params = IRequestOptions & (ITimeoutParams | IRetryParams);

let setMock: (body: IStationaryConsoleUpdateRequestBody, params?: Params & { returnResponse?: boolean }) => Promise<IStationaryConsoleUpdateResponseOK | Axios.AxiosResponse<IStationaryConsoleUpdateResponseOK>>;
let reactQuery: typeof ReactQuery;

export function stationaryConsoleUpdateSetMock(mock: (body: IStationaryConsoleUpdateRequestBody, params?: Params & { returnResponse?: boolean }) => Promise<IStationaryConsoleUpdateResponseOK | Axios.AxiosResponse<IStationaryConsoleUpdateResponseOK>>) {
  setMock = mock;
}

export async function stationaryConsoleUpdate(body: IStationaryConsoleUpdateRequestBody, params?: Params & { returnResponse?: false }):
  Promise<IStationaryConsoleUpdateResponseOK>;
export async function stationaryConsoleUpdate(body: IStationaryConsoleUpdateRequestBody, params: Params & { returnResponse: true }):
  Promise<Axios.AxiosResponse<IStationaryConsoleUpdateResponseOK>>;
export async function stationaryConsoleUpdate(body: IStationaryConsoleUpdateRequestBody, params?: Params & { returnResponse?: boolean }):
  Promise<IStationaryConsoleUpdateResponseOK | Axios.AxiosResponse<IStationaryConsoleUpdateResponseOK>> {
  if (!params) {
    params = {} as any;
  }
  if (setMock) {
    return setMock(body, params);
  }
  const client = params.client || getDefaultClient();
  if (!client) {
    throw new Error('No @ifit/gateway-core GatewayApiClient has been constructed yet! Please do so before calling gateway methods, or pass a compatible client in the parameters.');
  }
  const requestURL = `/console-store/v1/stationary-console`;
  const requestConfig = client.getRequestConfig(params);
  
  const headers: any = { ...requestConfig.headers, ...params.headers };
  const queryParams: any = { ...requestConfig.params };

  if ((params.forceStatusCheck) && (await consoleStoreIsDown(params))) {
    return null;
  }

  const response = await client.req.send<IStationaryConsoleUpdateResponseOK>(requestURL, {
    ...requestConfig,
    data: body,
    headers,
    method: 'PUT',
    params: queryParams,
  }, params);
  return params.returnResponse
    ? response
    : response.data;
}

export const useKeyStationaryConsoleUpdate = 'console-store:stationaryConsoleUpdate:put';

export function useMutationStationaryConsoleUpdate(
  mutationOptions?: ReactQuery.UseMutationOptions<IStationaryConsoleUpdateResponseOK, Axios.AxiosError<unknown>, { body: IStationaryConsoleUpdateRequestBody, params?: Params }>
): ReactQuery.UseMutationResult<IStationaryConsoleUpdateResponseOK, Axios.AxiosError<unknown>, { body: IStationaryConsoleUpdateRequestBody, params?: Params }, unknown> {
  if (!reactQuery) {
    reactQuery = require('react-query');
  }
  return reactQuery.useMutation(mutationFn, {
    mutationKey: useKeyStationaryConsoleUpdate,
    ...mutationOptions,
  });
}

function mutationFn({ body, params }: { body: IStationaryConsoleUpdateRequestBody, params?: Params }) {
  const promise = stationaryConsoleUpdate(body, params) as Promise<IStationaryConsoleUpdateResponseOK> & { cancel?: () => void; };
  if (params?.cancelSource) {
    promise.cancel = () => {
      params.cancelSource.cancel('Query was cancelled by React Query');
    };
  }
  return promise;
}
